"use client";

import React from "react";
import Image from "next/image";
import { unstable_ViewTransition as ViewTransition } from 'react'
import MethodCards from "@/components/utils/MethodCards";
import { InView } from "react-intersection-observer";
import NewsList from "@/components/utils/NewsList";
import AnchorLink from "@/components/utils/AnchorLink";
import InViewAnimation from "@/components/utils/InViewAnimation";
import Link from "next/link";
import SVG from "react-inlinesvg";

const MethodSection = ({posts}) => {
  
  return (
    <main className="page page-method-detail page-method-credit">
      <InView>
      {({ inView, ref, entry }) => (
        <section ref={ref} className={`section-method-kv container-l ${inView?"inViewDark":""}`}>
          <ViewTransition name="method-card-container-3">
            <div className="method-card">
              <div className="method-card-image">
                <img src="/assets/img/method/credit/kv-pic.jpg" alt="" />
              </div>
              <div className="method-card-inner">
                <div className="method-card-header">
                  <div className="badge en">
                    <span className="text">Method</span>
                    <span className="number">03</span>
                  </div>

                  <div className="method-card-shoulder bold">土づくりの好影響の価値化</div>
                  <h1 className="method-card-title en">
                    Next Green Credit
                  </h1>
                  <div className="method-card-description">
                    <div className="anchor-list">
                      <AnchorLink href="#SECTION-1" className="inner-link scaleHover">
                        <span>
                          <i className="icon">
                            <SVG src="/assets/img/common/icon/icon-arrow-anchor.svg" />
                          </i>
                          Next Green Creditとは
                        </span>
                      </AnchorLink>
                      <AnchorLink href="#SECTION-2" className="inner-link scaleHover">
                        <span>
                          <i className="icon">
                            <SVG src="/assets/img/common/icon/icon-arrow-anchor.svg" />
                          </i>
                          Next Green Creditの仕組み
                        </span>
                      </AnchorLink>
                      <AnchorLink href="#SECTION-3" className="inner-link scaleHover">
                        <span>
                          <i className="icon">
                            <SVG src="/assets/img/common/icon/icon-arrow-anchor.svg" />
                          </i>
                          国際規格ISO認証による高い信頼性
                        </span>
                      </AnchorLink>
                    </div>
                    <p className="anotation">北海道 赤井川コロポックル村周辺。<br className="pcOnly"/>山々が囲む盆地で、約1000人が畑とともに暮らしている。</p>
                  </div>
                  <div className="link-pdf-holder">
                    {/* <div className="link-pdf-holder-title">2025年秋 創出予定</div> */}
                    <a className="button-round button-pdf green size-xs scaleHover" href="https://docs.google.com/spreadsheets/d/1w7hmgyyTdsSq3tFFydDZ4ZTzS2nOkUsxRu2CTJbV_HI/edit?usp=sharing" target="_blank" rel="noopener noreferrer">
                      <div className="inner">
                        クレジット管理簿
                        <i className="icon">
                          <SVG src="/assets/img/common/icon/icon-outerlink.svg"/>
                        </i>
                      </div>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </ViewTransition>
        </section>
      )}
      </InView>

      <div className="container-m">
        <section className="section section-gallery">
          <div className="gallery-title bold">
            Agri LCA+での評価を基に、日本初の多面的な環境クレジットとして価値に変換。<br />
            いい土になるほど、新たな収入源に。
          </div>
          <div className="pic-block size-l">
            <InViewAnimation>
              <div className="pic round">
                <img src="/assets/img/method/credit/gallery/pic-1.jpg" alt="" loading="lazy" />
              </div>
              <div className="pic-text">
                北海道ニセコ町のLaLaLa Farmにて、シソの手入れの様子。土の湿り気や手触りを確かめながら状態を整える。
              </div>
            </InViewAnimation>
          </div>
        </section>

        <div className="separator-asterisk en">*</div>

        <section id="SECTION-1" className="section section-method-credit-1">
          <div className="section-title-holder">
            <h2 className="section-title bold">Next Green Creditとは</h2>
          </div>

          <div className="block n1 sticky-block-container grid-3-col reverse">
            <div className="sticky-item block-title-holder">
              <h2 className="block-title bold">
                日本初、脱炭素にとどまらない<br />
                農業の多面的な環境影響を<br />
                価値化
              </h2>
              <div className="block-lead bold">
                「Next Green Credit」は、脱炭素の枠を超え、「おいしく育つ土づくり」がもたらす多面的な好影響を価値化する、日本初の環境クレジットです。従来評価がされていなかった水質や生物多様性などの要素も含めて、土壌のもつ本質的な価値を定量的に表現します。
              </div>
            </div>
            <div className="static-item">
              <picture className="pic sp-wide">
                 <source srcSet="/assets/img/method/credit/section-1-pic.png" media="(min-width: 1024px)" />
                  <source srcSet="/assets/img/method/credit/section-1-pic-sp.png" media="(max-width: 1024px)" />
                <img src="/assets/img/method/credit/section-1-pic.png" alt="" loading="lazy" />
              </picture>
            </div>
          </div>
        </section>

        <div className="separator-asterisk en">*</div>
        
        <section id="SECTION-2" className="section section-method-credit-2">
          <div className="section-title-holder">
            <h2 className="section-title bold">
              Next Green Creditの<br className="spOnly" />仕組み
            </h2>
          </div>

          <div className="block n1 sticky-block-container grid-3-col">
            <div className="static-item block-title-holder">
              <h2 className="block-title bold">
                <span className="num">1.</span>さまざまな農業活動が<br />クレジットを創出
              </h2>
              <div className="block-lead bold">
                有機物の投入、化学肥料や農薬の削減、農業機械の利用削減など、土から健康な農業を目指すさまざまな農業活動によって生まれた価値を環境クレジットとして算出します。
              </div>

              <h2 id="SECTION-3" className="block-title bold">
                <span className="num">2.</span>国際規格ISO認証を受けた<br />信頼性の高い仕組み
              </h2>
              <div className="block-lead bold">
                温室効果ガス（GHG）の排出削減・吸収量は国際規格ISO14064-2に基づいて算定され、 その他の環境影響は国立研究開発法人 産業技術総合研究所（産総研）と共に開発した環境影響評価スキームによって算定されます。第三者機関によるGHG排出削減・吸収量の検証後にBGがクレジットを創出、企業に販売し、その収益を農家に還元します。
              </div>

              <div className="block-iso">
                <a className="block-iso-link bold scaleHover opacity" href="/assets/pdf/ISO_Statement.pdf" target="_blank" rel="noopener noreferrer">
                  <div className="block-iso-link-inner">
                    ISO14064-2の妥当性証明書
                    <i className="icon-outerlink">
                      <img src="/assets/img/common/icon/icon-outerlink.svg" alt="" />
                    </i>
                  </div>
                </a>
                <a className="block-iso-link bold scaleHover opacity" href="/assets/pdf/BG_Statement.pdf" target="_blank" rel="noopener noreferrer">
                  <div className="block-iso-link-inner">
                    <div className="text">BG声明書</div>
                    <i className="icon-outerlink">
                      <img src="/assets/img/common/icon/icon-outerlink.svg" alt="" />
                    </i>
                  </div>
                </a>
              </div>

              <h2 className="block-title bold">
                <span className="num">3.</span>クレジットの多様な価値が、<br />事業シナジーを創出
              </h2>
              <div className="block-lead bold">
                Next Green Creditは、生活者が享受するおいしい食と、購入企業の事業による環境貢献の接点として、企業価値向上に寄与します。そして、それが農業や食、土や水も守るサステナブルな未来を支えることにつながります。
              </div>
            </div>
            <div className="sticky-item">
              <picture className="pic sp-wide">
                 <source srcSet="/assets/img/method/credit/section-2-pic.png" media="(min-width: 1024px)" />
                  <source srcSet="/assets/img/method/credit/section-2-pic-sp.png" media="(max-width: 1024px)" />
                <img src="/assets/img/method/credit/section-2-pic.png" alt="" loading="lazy" />
              </picture>
            </div>
          </div>
        </section>

        <section className="section section-gallery height-wide">
          <div className="pic-block n1 left">
            <InViewAnimation>
              <div className="pic round">
                <img src="/assets/img/method/credit/gallery/pic-2.jpg" alt="" loading="lazy" />
              </div>
              <div className="pic-text">
                北海道の赤井川村コロポックル村にて、アスパラ出荷作業の様子。
              </div>
            </InViewAnimation>
          </div>
          <div className="pic-block n2 right">
            <InViewAnimation>
              <div className="pic round">
                <img src="/assets/img/method/credit/gallery/pic-3.jpg" alt="" loading="lazy" />
              </div>
              <div className="pic-text">
                北海道ニセコ町のLaLaLa Farmにて、昼休みのひととき。畑のそばで、昼食の準備。
              </div>
            </InViewAnimation>
          </div>
        </section>

        {posts.length > 0 && (
          <>
            <div className="separator-asterisk en">*</div>

            <section className="section section-method-latestcase">
              <div className="section-title-holder">
                <h2 className="section-title bold">
                  最新事例
                </h2>
              </div>
              <div className="container-xs grayCard">
                <NewsList posts={posts} />
                <Link href="/news/implementation/next_green_credit/" className="button-link button-round scaleHover size-s">
                  <span className="inner">すべての事例をみる</span>
                </Link>
              </div>
            </section>
          </>
        )}
      </div>
      
      <section className="section section-method-others">
        <div className="container-l">
          <MethodCards currentNumber={2} className="scaleHover" />
        </div>
      </section>
    </main>
  );
};

export default MethodSection;
